import { convertHtmlToText } from "./common";

type TinyMessageTemplate = {
  id?: number | null;
  template: string;
  subject_template: string;
  email_html_template: string;
  email_text_template?: string;
};

export default function formatMessageTemplate<
  T extends Partial<TinyMessageTemplate>
>(input: T) {
  const {
    template,
    subject_template,
    email_html_template,
    email_text_template
  } = input;
  return {
    ...input,
    template: normalizeTemplateValue(template),
    subject_template: normalizeTemplateValue(subject_template, true, true),
    email_html_template: normalizeTemplateValue(email_html_template, false),
    email_text_template: email_text_template
      ? normalizeTemplateValue(email_text_template)
      : undefined
  };
}

export const formatEmailTemplate = (input: any) => {
  const {
    template,
    subject_template,
    email_html_template,
    email_text_template
  } = input;

  return {
    ...input,
    template: normalizeTemplateValue(template, false),
    subject_template: normalizeTemplateValue(subject_template, true, true),
    email_html_template: normalizeTemplateValue(email_html_template, false),
    email_text_template: email_text_template
      ? normalizeTemplateValue(email_text_template)
      : normalizeTemplateValue(email_html_template)
  };
};

export const normalizeTemplateValue = (
  value: string | null | undefined,
  removeHtml: boolean = true,
  removeLineBreak: boolean = false
) => {
  if (value === undefined || value === null) return value;
  let result = (removeHtml ? convertHtmlToText(value) : value)
    .trim()
    .replaceAll("{{", "{")
    .replaceAll("}}", "}");

  if (removeLineBreak) {
    result = result.replaceAll("\r\n", "\n").replaceAll("\n", "");
  }

  return result;
};

export const transformStringToTemplateValue = (value: string) => {
  return value.replaceAll("{", "{{").replaceAll("}", "}}");
};

const SINGLE_BRACE_MERGE_TAG_REGEXP =
  /(^|[^{])\{((?:(?:Patient|Clinic|Practice|CurrentUser)\.[^{}]+)|CurrentDate(?:Time)?)}(?!})/g;

export const getTextTemplateSubjectValue = (value?: string | null) => {
  return convertHtmlToText(value)
    .replaceAll("\r", "")
    .replaceAll("\n", "")
    .replace(SINGLE_BRACE_MERGE_TAG_REGEXP, "$1{{$2}}");
};

/**
 * Checks if the given HTML content is a full HTML document (contains <html> tag)
 */
export const isFullHtmlDocument = (
  html: string | null | undefined
): boolean => {
  if (!html) return false;
  return /<html[\s>]/i.test(html);
};

/**
 * Wraps HTML content in a basic HTML document structure if it's not already a full document.
 * If the content already contains an <html> tag, returns it as-is.
 */
export const wrapHtmlForIframe = (
  html: string | null | undefined,
  options?: { wordBreak?: "break-all" | "break-word" }
): string => {
  const content = html ?? "";

  if (isFullHtmlDocument(content)) {
    return content;
  }

  const wordBreak = options?.wordBreak ?? "break-word";

  return `
    <!DOCTYPE html>
    <html>
      <head>
        <style>
          body {
            margin: 0;
            padding: 8px;
            font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
            word-break: ${wordBreak};
            word-wrap: break-word;
            font-size: 14px;
            line-height: 1.5;
          }
          img {
            max-width: 100%;
            height: auto;
            object-fit: contain;
          }
        </style>
      </head>
      <body>
        ${content}
      </body>
    </html>
  `;
};
